import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Volume2, Plus, Trash2, Edit3, X, Mic, MicOff, Sparkles, Save,
  Apple, GlassWater, Heart, Home, HelpCircle, Baby, Phone, Bath,
  UserRound, Utensils, DoorOpen, Building2, Store, Landmark,
  Contrast, TypeIcon, Zap,
} from "lucide-react";import {
  loadSpeak,
  addMessage,
  removeMessage,
  addVocab,
  removeVocab,
  updateAccessibility,
} from "@/lib/neuroSpeakStore";
import VoiceRecorder from "@/components/neurospeak/VoiceRecorder";

// -----------------------------------------------------------------------
// NeuroSpeak — AAC ("Communicate Now"), Message Builder, My Words, and
// Accessibility toggles.
// -----------------------------------------------------------------------

const CATEGORIES = [
  {
    key: "food",
    label: "Food",
    Icon: Utensils,
    color: "#f59e0b",
    starters: ["I want water", "I am thirsty", "I want to eat", "I am hungry", "Some tea please"],
  },
  {
    key: "pain",
    label: "Pain",
    Icon: Heart,
    color: "#f87171",
    starters: ["I am in pain", "My head hurts", "My back hurts", "It hurts here", "Please help me"],
  },
  {
    key: "family",
    label: "Family",
    Icon: UserRound,
    color: "#22d3ee",
    starters: ["Call my family", "I want to talk to my daughter", "Where is my son?", "Please tell my mother"],
  },
  {
    key: "bathroom",
    label: "Bathroom",
    Icon: Bath,
    color: "#a78bfa",
    starters: ["I need the bathroom", "Please help me to the bathroom", "I need privacy"],
  },
  {
    key: "help",
    label: "Help",
    Icon: HelpCircle,
    color: "#34d399",
    starters: ["I need help", "Please help me", "I need assistance", "I don't understand"],
  },
  {
    key: "emergency",
    label: "Emergency",
    Icon: Phone,
    color: "#ef4444",
    starters: ["This is an emergency", "Call an ambulance", "I feel very unwell", "Please call 108"],
  },
  {
    key: "feelings",
    label: "Feelings",
    Icon: Sparkles,
    color: "#f472b6",
    starters: ["I am tired", "I am worried", "I am sad", "I am okay", "I am happy"],
  },
  {
    key: "daily",
    label: "Daily needs",
    Icon: Home,
    color: "#60a5fa",
    starters: ["I am cold", "I am warm", "Please open the door", "Turn on the light", "I want to sleep"],
  },
];

const speak = (text) => {
  if (!text) return false;
  try {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      const u = new window.SpeechSynthesisUtterance(text);
      u.rate = 0.95;
      window.speechSynthesis.speak(u);
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
};

// =======================================================================
// COMMUNICATE NOW — the assistive AAC surface.
// =======================================================================

export function CommunicateNow({ big = false }) {
  const [category, setCategory] = useState(null);
  const [message, setMessage] = useState("");
  const [state, setState] = useState(() => loadSpeak());
  const [supported] = useState(
    typeof window !== "undefined" && "speechSynthesis" in window
  );
  const [lang] = useState(() => loadSpeak().profile?.language || "en");
  const msgClass = big ? "text-3xl" : "text-2xl";

  const trigger = () => {
    const ok = speak(message);
    if (!ok) {
      alert("Text-to-speech is not supported on this device / browser.");
    }
  };

  const onVoice = (data) => {
    const t = (data.transcript || "").trim();
    if (t) {
      setMessage(t);
      // Auto-speak so the user hears it in their own device's voice.
      setTimeout(() => speak(t), 250);
    }
  };

  return (
    <div>
      <div className="rounded-3xl border border-emerald-400/25 bg-emerald-400/5 p-6 sm:p-8">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          Communicate Now · 100% voice-only
        </div>
        <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
          Say what you need. <span className="italic text-emerald-300">Right now.</span>
        </h3>
        <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
          Tap a saved phrase, or tap the microphone and speak your own words.
          Your device will read the message back aloud — no keyboard needed.
        </p>

        {/* Current message display (read-only) */}
        <div
          data-testid="comm-message"
          className="mt-5 rounded-2xl border border-white/10 bg-black/40 p-4 min-h-[86px] flex items-center"
        >
          {message ? (
            <div className={`font-display font-semibold text-white leading-tight ${msgClass}`}>
              {message}
            </div>
          ) : (
            <div className="text-sm text-zinc-500">
              Tap a phrase or tap the microphone to start speaking…
            </div>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            {supported ? "Voice ready" : "Voice not supported"}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="comm-clear"
              onClick={() => setMessage("")}
              className="text-xs text-zinc-400 hover:text-white px-3 py-2"
            >
              Clear
            </button>
            <button
              data-testid="comm-speak"
              onClick={trigger}
              disabled={!message.trim()}
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400 text-black font-medium px-5 py-2 text-sm hover:bg-emerald-300 disabled:opacity-50"
            >
              <Volume2 className="w-4 h-4" /> Speak
            </button>
          </div>
        </div>

        {/* Voice recorder — the primary input */}
        <div className="mt-4">
          <VoiceRecorder
            target=""
            language={lang}
            onResult={onVoice}
            testId="comm-voice"
            ctaText="Tap to Speak your message"
            maxMs={10000}
          />
        </div>

        {/* Categories */}
        <div className="mt-6 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          Quick categories
        </div>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CATEGORIES.map((c) => {
            const on = category === c.key;
            return (
              <button
                key={c.key}
                data-testid={`comm-cat-${c.key}`}
                onClick={() => setCategory(c.key)}
                className={`rounded-2xl border ${
                  big ? "p-5" : "p-4"
                } text-left transition-colors`}
                style={{
                  borderColor: on ? c.color : "rgba(255,255,255,0.1)",
                  background: on ? `${c.color}20` : "rgba(255,255,255,0.03)",
                }}
              >
                <c.Icon
                  className={big ? "w-8 h-8" : "w-6 h-6"}
                  style={{ color: c.color }}
                />
                <div className={`mt-2 font-semibold text-white ${big ? "text-xl" : "text-base"}`}>
                  {c.label}
                </div>
              </button>
            );
          })}
        </div>

        {/* Emergency phrases (special section when Emergency category active) */}
        {category === "emergency" && (
          <div className="mt-5 rounded-2xl border border-red-500/40 bg-red-500/10 p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-red-300 font-semibold">
                  Your saved emergency phrases · one-tap replay
                </div>
                <p className="mt-1 text-xs text-red-100/80">
                  Record a personal emergency phrase in your own voice. Tap any
                  saved phrase to have your device speak it aloud.
                </p>
              </div>
              <a
                href="tel:108"
                data-testid="comm-emergency-call"
                className="inline-flex items-center gap-1.5 rounded-full bg-red-500 text-black text-xs font-semibold px-3 py-1.5"
              >
                <Phone className="w-3.5 h-3.5" /> Call 108
              </a>
            </div>

            {/* Saved emergency tiles */}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(state.messages || []).filter((m) => m.category === "emergency").length === 0 ? (
                <div className="col-span-full text-xs text-red-100/70 rounded-xl border border-red-500/20 bg-red-500/5 p-3">
                  No personal emergency phrases yet. Record one below — you can
                  save as many as you need (e.g. &ldquo;Please call my daughter&rdquo;,
                  &ldquo;I feel dizzy, help me sit down&rdquo;).
                </div>
              ) : (
                (state.messages || [])
                  .filter((m) => m.category === "emergency")
                  .map((m) => (
                    <button
                      key={m.id}
                      data-testid={`comm-emergency-tile-${m.id}`}
                      onClick={() => {
                        setMessage(m.text);
                        setTimeout(() => speak(m.text), 150);
                      }}
                      className={`text-left rounded-2xl border border-red-500/40 bg-red-500/15 hover:bg-red-500/25 text-white ${
                        big ? "px-5 py-5" : "px-4 py-4"
                      }`}
                    >
                      <div className="text-[10px] uppercase tracking-widest text-red-200 font-semibold flex items-center gap-1.5">
                        <Volume2 className="w-3 h-3" /> Tap to speak
                      </div>
                      <div className={`mt-1 font-semibold ${big ? "text-2xl" : "text-lg"}`}>
                        {m.text}
                      </div>
                    </button>
                  ))
              )}
            </div>

            {/* Voice recorder to add a new emergency phrase */}
            <div className="mt-4">
              <VoiceRecorder
                target=""
                language={lang}
                onResult={(data) => {
                  const t = (data.transcript || "").trim();
                  if (t) {
                    addMessage(t, "emergency");
                    setState(loadSpeak());
                    setMessage(t);
                    setTimeout(() => speak(t), 200);
                  }
                }}
                testId="comm-emergency-record"
                ctaText="Tap to Speak & Save Emergency Phrase"
                maxMs={10000}
              />
              <div className="mt-1 text-[10px] uppercase tracking-widest text-red-200/70 font-semibold">
                Your voice · saved on this device only
              </div>
            </div>
          </div>
        )}
        {category && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              Suggested phrases · tap to speak
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CATEGORIES.find((x) => x.key === category).starters.map((s) => (
                <button
                  key={s}
                  data-testid={`comm-phrase-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => {
                    setMessage(s);
                    setTimeout(() => speak(s), 150);
                  }}
                  className={`rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white text-left ${
                    big ? "px-4 py-4 text-xl" : "px-3 py-2.5 text-base"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =======================================================================
// PERSONAL MESSAGE BUILDER
// =======================================================================

export function MessageBuilder() {
  const [state, setState] = useState(() => loadSpeak());
  const [draft, setDraft] = useState("");
  const [cat, setCat] = useState("custom");
  const [lang] = useState(() => loadSpeak().profile?.language || "en");

  const refresh = () => setState(loadSpeak());

  const onVoice = (data) => {
    setDraft((data.transcript || "").trim());
  };

  const save = () => {
    if (!draft.trim()) return;
    addMessage(draft.trim(), cat);
    setDraft("");
    refresh();
  };

  const remove = (id) => {
    removeMessage(id);
    refresh();
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold">
        Personal message builder · voice-only
      </div>
      <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
        Your everyday phrases, one tap away.
      </h3>
      <p className="mt-2 text-xs text-zinc-500 max-w-2xl">
        Tap the microphone, say the phrase, then tap <b>Save</b>. Whisper AI
        writes it down for you — no keyboard needed.
      </p>

      {/* Category chips */}
      <div className="mt-4 flex flex-wrap gap-2">
        {["custom", "help", "family", "food", "feelings", "daily", "emergency"].map((c) => (
          <button
            key={c}
            data-testid={`mb-cat-${c}`}
            onClick={() => setCat(c)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-widest ${
              cat === c
                ? "border-cyan-400 bg-cyan-400/20 text-white"
                : "border-white/10 bg-white/5 hover:bg-white/10 text-zinc-300"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Voice recorder */}
      <div className="mt-4">
        <VoiceRecorder
          target=""
          language={lang}
          onResult={onVoice}
          testId="mb-voice"
          ctaText="Tap to Speak your phrase"
          maxMs={10000}
        />
      </div>

      {/* Draft preview */}
      {draft && (
        <div className="mt-4 rounded-2xl border border-cyan-400/25 bg-cyan-400/5 p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
              Draft phrase
            </div>
            <div data-testid="mb-draft" className="mt-1 text-lg font-semibold text-white truncate">
              {draft}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              data-testid="mb-draft-speak"
              onClick={() => speak(draft)}
              className="w-9 h-9 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center"
            >
              <Volume2 className="w-4 h-4 text-cyan-300" />
            </button>
            <button
              data-testid="mb-add"
              onClick={save}
              className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500 text-black font-medium px-4 py-2 text-sm hover:bg-cyan-400"
            >
              <Plus className="w-4 h-4" /> Save
            </button>
          </div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-2">
        {(state.messages || []).map((m) => (
          <div
            key={m.id}
            data-testid={`mb-item-${m.id}`}
            className="rounded-2xl border border-white/10 bg-black/30 p-3 flex items-center justify-between gap-2"
          >
            <div className="min-w-0">
              <div className="text-sm text-white truncate">{m.text}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                {m.category}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => speak(m.text)}
                data-testid={`mb-speak-${m.id}`}
                className="w-9 h-9 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center"
              >
                <Volume2 className="w-4 h-4 text-cyan-300" />
              </button>
              <button
                onClick={() => remove(m.id)}
                data-testid={`mb-remove-${m.id}`}
                className="w-9 h-9 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center"
              >
                <Trash2 className="w-4 h-4 text-red-300" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =======================================================================
// MY WORDS — personal vocabulary manager
// =======================================================================

const VOCAB_META = [
  { key: "family", label: "Family", color: "#22d3ee" },
  { key: "food", label: "Food", color: "#f59e0b" },
  { key: "places", label: "Places", color: "#a78bfa" },
  { key: "daily", label: "Daily life", color: "#34d399" },
  { key: "personal", label: "Personal", color: "#f472b6" },
];

export function VocabManager() {
  const [state, setState] = useState(() => loadSpeak());
  const [cat, setCat] = useState("family");
  const [draft, setDraft] = useState("");
  const [lang] = useState(() => loadSpeak().profile?.language || "en");
  const refresh = () => setState(loadSpeak());

  const onVoice = (data) => {
    setDraft((data.transcript || "").trim());
  };
  const save = () => {
    if (!draft.trim()) return;
    addVocab(cat, draft.trim());
    setDraft("");
    refresh();
  };
  const remove = (word) => {
    removeVocab(cat, word);
    refresh();
  };

  const list = state.vocab?.[cat] || [];

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
        My words · voice-only vocabulary
      </div>
      <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
        Train with the words that matter to you.
      </h3>
      <p className="mt-2 text-xs text-zinc-500">
        Tap the microphone, say the word, then tap <b>Add</b>. Stored on this
        device only. Consent-controlled sharing.
      </p>

      <div className="mt-5 flex flex-wrap gap-2">
        {VOCAB_META.map((v) => {
          const on = cat === v.key;
          return (
            <button
              key={v.key}
              data-testid={`vocab-cat-${v.key}`}
              onClick={() => setCat(v.key)}
              className="rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-widest"
              style={{
                borderColor: on ? v.color : "rgba(255,255,255,0.1)",
                background: on ? `${v.color}20` : "transparent",
                color: on ? "#fff" : v.color,
              }}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        <VoiceRecorder
          target=""
          language={lang}
          onResult={onVoice}
          testId="vocab-voice"
          ctaText={`Tap to Speak a ${cat} word`}
          maxMs={6000}
        />
      </div>

      {draft && (
        <div className="mt-3 rounded-2xl border border-cyan-400/25 bg-cyan-400/5 p-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
              New word
            </div>
            <div className="mt-1 text-lg font-semibold text-white truncate">
              {draft}
            </div>
          </div>
          <button
            data-testid="vocab-add"
            onClick={save}
            className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500 text-black font-medium px-4 py-2 text-sm hover:bg-cyan-400"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {list.length === 0 && (
          <div className="text-xs text-zinc-500">No words yet in this category.</div>
        )}
        {list.map((v) => (
          <div
            key={v.word}
            data-testid={`vocab-item-${v.word}`}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 pl-3 pr-1.5 py-1.5 text-sm text-white"
          >
            {v.word}
            <button
              onClick={() => speak(v.word)}
              data-testid={`vocab-speak-${v.word}`}
              className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
            >
              <Volume2 className="w-3.5 h-3.5 text-cyan-300" />
            </button>
            <button
              onClick={() => remove(v.word)}
              data-testid={`vocab-remove-${v.word}`}
              className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
            >
              <X className="w-3.5 h-3.5 text-red-300" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// =======================================================================
// ACCESSIBILITY toggles + language reminder
// =======================================================================

export function AccessibilityPanel({ onChange }) {
  const [s, setS] = useState(() => loadSpeak());
  const acc = s.profile?.accessibility || {};

  const toggle = (key) => {
    const patch = { [key]: !acc[key] };
    updateAccessibility(patch);
    const next = loadSpeak();
    setS(next);
    onChange?.(next);
  };

  const items = [
    { key: "large_text", label: "Large text", Icon: TypeIcon },
    { key: "high_contrast", label: "High contrast", Icon: Contrast },
    { key: "audio_cues", label: "Audio cues", Icon: Volume2 },
    { key: "simple_ui", label: "Simple UI (big buttons)", Icon: Zap },
  ];

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 mt-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
        Accessibility
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {items.map((i) => {
          const on = !!acc[i.key];
          return (
            <button
              key={i.key}
              data-testid={`speak-a11y-${i.key}`}
              onClick={() => toggle(i.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                on
                  ? "border-cyan-400 bg-cyan-400/20 text-white"
                  : "border-white/10 bg-white/5 hover:bg-white/10 text-zinc-300"
              }`}
            >
              <i.Icon className="w-3.5 h-3.5" />
              {i.label}
              <span className="text-[10px] opacity-60">{on ? "ON" : "OFF"}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =======================================================================
// Optional Speech Input — Web Speech API with graceful fallback.
// =======================================================================

export function SpeechInput({ onResult }) {
  const [supported] = useState(
    typeof window !== "undefined" &&
      ("SpeechRecognition" in window || "webkitSpeechRecognition" in window)
  );
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);

  const start = () => {
    if (!supported) return;
    try {
      const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
      const rec = new Ctor();
      rec.lang = "en-IN";
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      rec.onresult = (e) => {
        const t = e.results[0][0].transcript;
        setListening(false);
        onResult?.(t);
      };
      rec.onerror = () => {
        setListening(false);
        setError("Recognition unavailable. Please type your answer.");
      };
      rec.onend = () => setListening(false);
      rec.start();
      recRef.current = rec;
      setListening(true);
      setError(null);
    } catch {
      setError("Recognition unavailable. Please type your answer.");
    }
  };

  const stop = () => {
    try {
      recRef.current?.stop();
    } catch {
      /* ignore */
    }
    setListening(false);
  };

  if (!supported) {
    return (
      <div className="text-[11px] text-zinc-500 mt-2">
        Voice input isn&apos;t supported on this device — please type instead.
      </div>
    );
  }

  return (
    <div className="mt-2">
      <button
        data-testid="speech-input-toggle"
        onClick={listening ? stop : start}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
          listening
            ? "border-red-400 bg-red-400/20 text-white animate-pulse"
            : "border-white/10 bg-white/5 hover:bg-white/10 text-zinc-200"
        }`}
      >
        {listening ? (
          <>
            <MicOff className="w-3.5 h-3.5" /> Stop
          </>
        ) : (
          <>
            <Mic className="w-3.5 h-3.5" /> Speak your answer
          </>
        )}
      </button>
      {error && <div className="text-[11px] text-amber-300 mt-1">{error}</div>}
    </div>
  );
}
